import type { CropData, PrintRatio } from '@/types';
import type { Area, Point } from 'react-easy-crop';
import { PRINT_RATIOS } from '@/types';

/**
 * Crop State Interface
 * Matches the internal state structure used by the Cropper component
 */
export interface CropState {
  crop: Point;
  zoom: number;
  croppedAreaPixels: Area | null;
}

/**
 * Transform crop states to CropData format
 * 
 * Converts the internal cropper state (with Point and Area objects)
 * into the standardized CropData format used throughout the application.
 * 
 * @param cropStates - Record of crop states for each print ratio
 * @param orientation - Global orientation applied to all crops
 * @returns Array of CropData objects (only includes completed crops)
 */
export function transformCropStatesToCropData(
  cropStates: Record<PrintRatio, CropState>,
  orientation: 'portrait' | 'landscape'
): CropData[] {
  return PRINT_RATIOS.map(ratio => {
    const state = cropStates[ratio];
    const pixels = state.croppedAreaPixels;

    if (!pixels) {
      return null;
    }

    return {
      ratio,
      x: pixels.x,
      y: pixels.y,
      width: pixels.width,
      height: pixels.height,
      orientation,
    };
  }).filter(Boolean) as CropData[];
}

/**
 * Validate that all crop ratios are completed
 * 
 * @param cropStates - Record of crop states for each print ratio
 * @returns true if all 5 ratios have croppedAreaPixels
 */
export function validateAllCropsComplete(
  cropStates: Record<PrintRatio, CropState>
): boolean {
  return PRINT_RATIOS.every(
    ratio => cropStates[ratio].croppedAreaPixels !== null
  );
}

/**
 * Count completed crops
 * 
 * @param cropStates - Record of crop states for each print ratio
 * @returns Number of completed crops (0-5)
 */
export function countCompletedCrops(
  cropStates: Record<PrintRatio, CropState>
): number {
  return PRINT_RATIOS.filter(
    ratio => cropStates[ratio].croppedAreaPixels !== null
  ).length;
}

